/**
 * Helpers de landings comerciales (Ola 1.2).
 * Resuelve categoría / colección / marca → landing TenantExperience + SEO + breadcrumb.
 */
import { slugifyName } from "@/lib/slug";
import { storefrontPath } from "@/lib/tenant-host-paths";
import type {
  CatalogLandingConfig,
  ExperiencePlp,
  TenantExperienceDocument,
} from "@/lib/experience-definition/types";
import type { MarketCatalogData, MarketCategory, MarketProduct } from "@/lib/market-catalog";

export type LandingKind = "category" | "collection" | "brand";

export function promoPath(storeSlug: string, promoId: string): string {
  return storefrontPath(storeSlug, `/promo/${promoId}`);
}

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

export function landingPath(storeSlug: string, kind: LandingKind, targetSlug: string): string {
  if (kind === "category") return storefrontPath(storeSlug, `/categoria/${targetSlug}`);
  if (kind === "collection") return storefrontPath(storeSlug, `/coleccion/${targetSlug}`);
  return storefrontPath(storeSlug, `/marca/${targetSlug}`);
}

/** PLP — catálogo comercial puro (ruta independiente). */
export function catalogPath(
  storeSlug: string,
  query?: {
    categoria?: string;
    q?: string;
    marca?: string;
    coleccion?: string;
    /** id de oferta/promo, o `"all"` para el set agregado "En promoción". */
    promo?: string;
  },
): string {
  const base = storefrontPath(storeSlug, "/catalogo");
  if (!query) return base;
  const params = new URLSearchParams();
  if (query.categoria) params.set("categoria", query.categoria);
  if (query.q) params.set("q", query.q);
  if (query.marca) params.set("marca", query.marca);
  if (query.coleccion) params.set("coleccion", query.coleccion);
  if (query.promo) params.set("promo", query.promo);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function staticPagePath(storeSlug: string, pageSlug: string): string {
  return storefrontPath(storeSlug, `/pagina/${pageSlug}`);
}

/** Expande hrefs cortos del Experience a URLs absolutas del storefront. */
export function expandStorefrontHref(storeSlug: string, href: string | undefined | null): string | undefined {
  if (!href) return undefined;
  const raw = href.trim();
  if (!raw || raw === "#" || raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("mailto:") || raw.startsWith("tel:")) {
    return raw || undefined;
  }
  if (raw.startsWith(`/market/${storeSlug}`)) return storefrontPath(storeSlug, raw);
  if (raw.startsWith("/market/")) return raw;
  if (raw.startsWith("/institucional/")) return raw;
  if (/^\/bundles\/?$/i.test(raw)) return storefrontPath(storeSlug, "/bundles");
  const cat = raw.match(/^\/(?:c|categoria)\/([^/?#]+)/i);
  if (cat) return landingPath(storeSlug, "category", decodeURIComponent(cat[1]));
  const col = raw.match(/^\/(?:coleccion|collection)\/([^/?#]+)/i);
  if (col) return landingPath(storeSlug, "collection", decodeURIComponent(col[1]));
  const brand = raw.match(/^\/(?:b|marca|brand)\/([^/?#]+)/i);
  if (brand) return landingPath(storeSlug, "brand", decodeURIComponent(brand[1]));
  if (raw === "/promos" || raw === "/ofertas") return catalogPath(storeSlug, { promo: "all" });
  if (raw === "/catalogo" || raw === "/plp" || raw.startsWith("/catalogo?")) {
    const q = raw.includes("?") ? raw.slice(raw.indexOf("?")) : "";
    return `${storefrontPath(storeSlug, "/catalogo")}${q}`;
  }
  const staticPage = raw.match(/^\/pagina\/([^/?#]+)/i);
  if (staticPage) return storefrontPath(storeSlug, `/pagina/${decodeURIComponent(staticPage[1])}`);
  if (raw === "/" || raw === "/home") return storefrontPath(storeSlug);
  if (raw.startsWith("/#") || raw.startsWith("#")) {
    const hash = raw.startsWith("#") ? raw : raw.slice(1);
    return `${storefrontPath(storeSlug)}${hash.startsWith("#") ? hash : `#${hash}`}`;
  }
  if (raw.startsWith("/")) return storefrontPath(storeSlug, raw);
  return raw;
}



/** Resuelve ExperienceLink (href corto o page) a URL absoluta del storefront. */
export function resolveExperienceLinkHref(
  storeSlug: string,
  link?: {
    href?: string;
    page?: string;
    staticPageSlug?: string;
    scrollTo?: string;
    categoryId?: string;
    collectionId?: string;
    brandName?: string;
  } | null,
): string | undefined {
  if (!link) return undefined;
  if (link.href) return expandStorefrontHref(storeSlug, link.href);
  if (link.scrollTo) {
    const id = link.scrollTo.replace(/^#/, "");
    return `${storefrontPath(storeSlug)}#${id}`;
  }
  switch (link.page) {
    case "home":
      return storefrontPath(storeSlug);
    case "plp":
      return catalogPath(storeSlug);
    case "cart":
      return `${storefrontPath(storeSlug)}#carrito`;
    case "account":
      return storefrontPath(storeSlug, "/cuenta");
    case "wishlist":
      return catalogPath(storeSlug);
    case "static":
      return link.staticPageSlug
        ? staticPagePath(storeSlug, link.staticPageSlug)
        : undefined;
    case "category":
      return link.categoryId
        ? landingPath(storeSlug, "category", link.categoryId)
        : catalogPath(storeSlug);
    case "collection":
      return link.collectionId
        ? landingPath(storeSlug, "collection", link.collectionId)
        : catalogPath(storeSlug);
    case "brand":
      return link.brandName
        ? landingPath(storeSlug, "brand", link.brandName)
        : catalogPath(storeSlug);
    default:
      return undefined;
  }
}

export function findCategoryByParam(categories: MarketCategory[], param: string): MarketCategory | null {
  const key = param.trim().toLowerCase();
  return categories.find((c) => c.slug === key || c.id === key || slugifyName(c.name) === key) ?? null;
}

export function findBrandByParam(products: MarketProduct[], param: string): string | null {
  const key = decodeURIComponent(param).trim().toLowerCase();
  const brands = new Set(products.map((p) => p.brandName?.trim()).filter((b): b is string => Boolean(b)));
  for (const b of brands) {
    if (b.toLowerCase() === key || slugifyName(b) === key) return b;
  }
  return null;
}

export function findCollectionTag(products: MarketProduct[], param: string): string | null {
  const key = decodeURIComponent(param).trim().toLowerCase();
  const tags = new Set(products.flatMap((p) => p.tags));
  for (const t of tags) {
    if (t.toLowerCase() === key || slugifyName(t) === key) return t;
  }
  // Also match landing.targetId / landing.slug
  return null;
}

export function resolveLandingConfig(
  plp: ExperiencePlp,
  kind: LandingKind,
  targetId: string,
): CatalogLandingConfig | null {
  const specific = plp.landings.find(
    (l) =>
      l.enabled &&
      l.kind === kind &&
      (l.targetId === targetId || l.slug === targetId || slugifyName(l.targetId) === slugifyName(targetId)),
  );
  if (specific) return specific;

  const defaults =
    kind === "category"
      ? plp.landingDefaults.category
      : kind === "collection"
        ? plp.landingDefaults.collection
        : (plp.landingDefaults.brand ?? plp.landingDefaults.collection);

  return {
    id: `default-${kind}-${targetId}`,
    targetId,
    kind,
    enabled: defaults.enabled,
    hero: defaults.hero,
    banner: defaults.banner,
    description: defaults.description,
    editorialHtml: defaults.editorialHtml,
    featuredProductIds: defaults.featuredProductIds,
    relatedCollectionIds: defaults.relatedCollectionIds,
    faq: defaults.faq,
    seo: defaults.seo ?? {},
    defaultSort: defaults.defaultSort,
    layout: defaults.layout,
  };
}

export function filterProductsForLanding(
  products: MarketProduct[],
  kind: LandingKind,
  target: { categoryId?: string | null; collectionTag?: string | null; brandName?: string | null },
): MarketProduct[] {
  if (kind === "category" && target.categoryId) {
    if (target.categoryId === "combos" || target.categoryId === "bundles") return products; // handled separately
    return products.filter((p) => p.categoriaId === target.categoryId);
  }
  if (kind === "collection" && target.collectionTag) {
    const tag = target.collectionTag.toLowerCase();
    return products.filter((p) => p.tags.some((t) => t.toLowerCase() === tag));
  }
  if (kind === "brand" && target.brandName) {
    const b = target.brandName.toLowerCase();
    return products.filter((p) => (p.brandName ?? "").toLowerCase() === b);
  }
  return products;
}

export function buildLandingBreadcrumbs(args: {
  storeSlug: string;
  storeName: string;
  kind: LandingKind;
  title: string;
  verticalLabel?: string | null;
}): BreadcrumbItem[] {
  const { storeSlug, title } = args;
  return [
    { label: "Inicio", href: storefrontPath(storeSlug) },
    { label: title },
  ];
}

export function verticalNavLabel(
  doc: TenantExperienceDocument | null | undefined,
  businessProfile?: string | null,
): string | null {
  const preset = doc?.navigation.verticalPreset;
  if (preset === "fashion") return "Moda";
  if (preset === "restaurant" || preset === "fast_food") return "Carta";
  if (preset === "pharmacy") return "Farmacia";
  if (preset === "bazar") return "Bazar";
  if (preset === "pet") return "Pet Shop";
  if (businessProfile === "fashion") return "Moda";
  if (businessProfile === "rotiseria" || businessProfile === "restaurant") return "Carta";
  if (businessProfile === "farmacia") return "Farmacia";
  return null;
}

export function landingSeoHead(args: {
  storeName: string;
  landing: CatalogLandingConfig;
  fallbackTitle: string;
  fallbackDescription?: string | null;
  canonicalPath: string;
  origin?: string;
}) {
  const title = args.landing.seo.title?.trim() || `${args.fallbackTitle} — ${args.storeName}`;
  const description =
    args.landing.seo.description?.trim() ||
    args.landing.description?.trim() ||
    args.fallbackDescription ||
    `Explorá ${args.fallbackTitle} en ${args.storeName}`;
  const ogImage = args.landing.seo.ogImage?.src || args.landing.hero.media?.src;
  const canonical =
    args.landing.seo.canonical || (args.origin ? `${args.origin}${args.canonicalPath}` : args.canonicalPath);

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
  ];
  if (ogImage) meta.push({ property: "og:image", content: ogImage });

  const links: Array<Record<string, string>> = [{ rel: "canonical", href: canonical }];

  const faq = args.landing.faq ?? [];
  const schemaType = args.landing.seo.schemaType ?? (faq.length ? "FAQPage" : "CollectionPage");
  let jsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": schemaType,
    name: title,
    description,
  };
  if (schemaType === "FAQPage" && faq.length) {
    jsonLd = {
      ...jsonLd,
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    };
  }

  return { title, description, meta, links, jsonLd, ogImage };
}

/** Forma "resuelta" de una landing — la que consume la UI (hero/banner ya aplanados). */
export type ResolvedLandingView = {
  title: string;
  description: string | null;
  hero: {
    enabled: boolean;
    imageUrl?: string | null;
    videoUrl?: string | null;
    headline?: string;
    subheadline?: string;
    ctaLabel?: string;
    countdownEndsAt?: string;
    campaignLabel?: string;
  } | null;
  banner: {
    imageUrl?: string | null;
    headline?: string;
    subheadline?: string;
    ctaLabel?: string;
  } | null;
  editorialHtml: string | null;
  seoFooter: string | null;
};

/**
 * Aplana un `CatalogLandingConfig` ya resuelto (specific u default, ver
 * `resolveLandingConfig`) a la forma que consume `MarketCatalogSection` para
 * pintar hero/banner — mismo shape que producía `resolveCatalogLanding`.
 */
export function renderLandingConfig(args: {
  landing: CatalogLandingConfig;
  fallbackTitle: string;
  fallbackImage?: string | null;
  plpHeroEnabled: boolean;
  plpShowBanner: boolean;
}): ResolvedLandingView {
  const { landing, fallbackTitle, fallbackImage, plpHeroEnabled, plpShowBanner } = args;
  const heroEnabled = plpHeroEnabled && landing.hero.enabled;
  const media = landing.hero.media;
  const bannerMedia = landing.banner.media;

  const headline = landing.hero.headline || fallbackTitle;
  const rawDescription = landing.description?.trim() ?? null;
  const description =
    rawDescription && rawDescription.toLowerCase() !== headline.trim().toLowerCase() ? rawDescription : null;

  return {
    title: headline,
    description,
    hero: heroEnabled
      ? {
          enabled: true,
          imageUrl: media?.kind === "image" ? media.src : (fallbackImage ?? null),
          videoUrl: media?.kind === "video" ? media.src : null,
          headline: landing.hero.headline || fallbackTitle,
          subheadline: landing.hero.subheadline,
          ctaLabel: landing.hero.cta?.label,
          countdownEndsAt: landing.hero.countdown?.endsAt,
          campaignLabel: landing.hero.campaignLabel,
        }
      : null,
    banner:
      plpShowBanner && landing.banner.enabled && (bannerMedia || landing.banner.headline)
        ? {
            imageUrl: bannerMedia?.kind === "image" ? bannerMedia.src : null,
            headline: landing.banner.headline,
            subheadline: landing.banner.subheadline,
            ctaLabel: landing.banner.cta?.label,
          }
        : null,
    editorialHtml: landing.editorialHtml ?? null,
    seoFooter: landing.seo.footerCopy ?? null,
  };
}

/**
 * Resuelve el target de una colección a partir del param de URL: primero
 * busca una landing de colección con `slug` propio (el comercio puede armar
 * una URL linda distinta del tag), y si no hay, cae al tag de producto.
 */
export function resolveCollectionTarget(
  products: MarketProduct[],
  plp: ExperiencePlp | null,
  param: string,
): { tag: string; slug: string } | null {
  const key = decodeURIComponent(param).trim().toLowerCase();
  if (plp) {
    const bySlug = plp.landings.find(
      (l) =>
        l.enabled &&
        l.kind === "collection" &&
        (l.slug?.trim().toLowerCase() === key ||
          l.targetId.toLowerCase() === key ||
          slugifyName(l.targetId) === key),
    );
    if (bySlug) {
      return { tag: bySlug.targetId, slug: bySlug.slug?.trim() || slugifyName(bySlug.targetId) };
    }
  }
  const tag = findCollectionTag(products, param);
  if (tag) return { tag, slug: slugifyName(tag) };
  return null;
}

export function listBrandNames(products: MarketProduct[]): string[] {
  const map = new Map<string, number>();
  for (const p of products) {
    const b = p.brandName?.trim();
    if (!b) continue;
    map.set(b, (map.get(b) ?? 0) + 1);
  }
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "es"))
    .map(([name]) => name);
}

export function getPublishedPlp(data: MarketCatalogData): ExperiencePlp | null {
  return data.experiencePublished?.plp ?? null;
}
